const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Resolve a menu image URL: if the stored value is a relative path like
 * /api/menu/images/xxx.jpg we prefix the API base URL so it loads correctly
 * regardless of which host/domain stored it.
 */
export function resolveImageUrl(url) {
  if (!url) return null;
  // Normalise legacy /uploads/ paths → /api/uploads/ so they go through the API proxy
  const normalised = url.replace(/^(https?:\/\/[^/]+)?\/uploads\//, (_, host) =>
    host ? `${host}/api/uploads/` : '/api/uploads/'
  );
  if (normalised.startsWith('http://') || normalised.startsWith('https://')) {
    // Could be a stale absolute URL from old backend — strip the host and re-prefix
    try {
      const parsed = new URL(normalised);
      return `${API_BASE_URL}${parsed.pathname}`;
    } catch {
      return normalised;
    }
  }
  // Relative path — prefix with API base
  return `${API_BASE_URL}${normalised}`;
}
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('customer_auth_token');
    }
    return this.token;
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('customer_auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('customer_auth_token');
    localStorage.removeItem('customer_data');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
      config.body = JSON.stringify(config.body);
    }

    if (config.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || data.error || `Request failed with status ${response.status}`,
        };
      }

      if (data.hasOwnProperty('success')) {
        return data;
      }

      return { success: true, data };
    } catch (error) {
      console.error('API Request Error:', error);
      return { success: false, error: error.message || 'Network request failed' };
    }
  }

  // ─── Auth ─────────────────────────────────────────────────────────────────

  async sendLoginOTP(phone, countryCode = '+1') {
    return this.request('/api/customer/auth/send-otp', {
      method: 'POST',
      body: { phone, country_code: countryCode },
    });
  }

  async verifyLoginOTP(phone, code, countryCode = '+1') {
    return this.request('/api/customer/auth/verify-otp', {
      method: 'POST',
      body: { phone, code, country_code: countryCode },
    });
  }

  async sendRegistrationOTP(phone, countryCode = '+1') {
    return this.request('/api/customer/auth/send-registration-otp', {
      method: 'POST',
      body: { phone, country_code: countryCode },
    });
  }

  async verifyRegistrationOTP(phone, code) {
    return this.request('/api/customer/auth/verify-registration-otp', {
      method: 'POST',
      body: { phone, code },
    });
  }

  async completeRegistration(data) {
    return this.request('/api/customer/auth/complete-registration', {
      method: 'POST',
      body: data,
    });
  }

  async firebasePhoneAuth(firebaseToken) {
    return this.request('/api/customer/auth/firebase-phone-auth', {
      method: 'POST',
      body: { firebase_token: firebaseToken },
    });
  }

  async completeFirebaseRegistration(data) {
    return this.request('/api/customer/auth/firebase-complete-registration', {
      method: 'POST',
      body: data,
    });
  }

  async login(username, password) {
    return this.request('/api/customer/auth/login', {
      method: 'POST',
      body: { username, password },
    });
  }

  async gatewayLogin(username, password) {
    return this.request('/api/customer/gateway/login', {
      method: 'POST',
      body: { username, password },
    });
  }

  async logout() {
    return this.request('/api/customer/auth/logout', { method: 'POST' });
  }

  async refreshToken() {
    return this.request('/api/customer/auth/refresh', { method: 'POST' });
  }

  // ─── Profile ──────────────────────────────────────────────────────────────

  async getProfile() {
    return this.request('/api/customer/auth/profile');
  }

  async updateProfile(data) {
    return this.request('/api/customer/auth/profile', { method: 'PUT', body: data });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/api/customer/auth/change-password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    });
  }

  async deleteAccount(reason = '') {
    return this.request('/api/customer/auth/delete-account', {
      method: 'DELETE',
      body: { reason },
    });
  }

  async sendEmailOtp(email) {
    return this.request('/api/customer/auth/send-email-otp', { method: 'POST', body: { email } });
  }

  async verifyEmailOtp(email, otp) {
    return this.request('/api/customer/auth/verify-email-otp', { method: 'POST', body: { email, otp } });
  }

  async uploadProfilePicture(file) {
    const formData = new FormData();
    formData.append('profile_image', file);
    const token = this.getToken();
    const response = await fetch(`${this.baseURL}/api/customer/auth/profile/picture`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    return response.json();
  }

  async deleteProfilePicture() {
    return this.request('/api/customer/auth/profile/picture', { method: 'DELETE' });
  }

  // ─── Addresses ────────────────────────────────────────────────────────────

  async getAddresses() {
    return this.request('/api/customer/addresses');
  }

  async createAddress(data) {
    return this.request('/api/customer/addresses', { method: 'POST', body: data });
  }

  async updateAddress(id, data) {
    return this.request(`/api/customer/addresses/${id}`, { method: 'PUT', body: data });
  }

  async deleteAddress(id) {
    return this.request(`/api/customer/addresses/${id}`, { method: 'DELETE' });
  }

  async setDefaultAddress(id) {
    return this.request(`/api/customer/addresses/${id}/set-default`, { method: 'PUT' });
  }

  // ─── Discovery ────────────────────────────────────────────────────────────

  async getCities() {
    return this.request('/api/customer/discovery/cities');
  }

  async getKitchens(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/api/customer/discovery/kitchens${queryString ? `?${queryString}` : ''}`);
  }

  async getKitchenById(id) {
    return this.request(`/api/customer/discovery/kitchen/${id}`);
  }

  async getKitchenMenu(kitchenId) {
    return this.request(`/api/customer/kitchens/${kitchenId}/menu`);
  }

  async getKitchenAvailability(kitchenId) {
    return this.request(`/api/customer/kitchens/${kitchenId}/availability`);
  }

  async getKitchenPickupSlots(kitchenId, dateStr) {
    return this.request(`/api/customer/kitchens/${kitchenId}/slots?date=${dateStr}`);
  }

  async validatePickupSlot(kitchenId, dateStr, timeStr) {
    return this.request(`/api/customer/kitchens/${kitchenId}/validate-slot`, {
      method: 'POST',
      body: { date: dateStr, time: timeStr },
    });
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  async getSearchSuggestions(query, lat = null, lon = null) {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (lat) params.append('lat', lat);
    if (lon) params.append('lon', lon);
    params.append('limit', 20);
    return this.request(`/api/customer/search/suggestions?${params.toString()}`);
  }

  async searchByDish(dishName, options = {}) {
    const params = new URLSearchParams();
    params.append('limit', options.limit || 50);
    params.append('offset', options.offset || 0);
    if (options.lat) params.append('lat', options.lat);
    if (options.lon) params.append('lon', options.lon);
    if (options.maxDistance) params.append('max_distance', options.maxDistance);
    if (options.sortBy) params.append('sort_by', options.sortBy);
    if (options.vegOnly) params.append('veg_only', 'true');
    return this.request(`/api/customer/search/dish/${encodeURIComponent(dishName)}?${params.toString()}`);
  }

  async searchByLocation(options = {}) {
    const params = new URLSearchParams();
    if (options.city) params.append('city', options.city);
    if (options.zip) params.append('zip', options.zip);
    if (options.lat) params.append('lat', options.lat);
    if (options.lon) params.append('lon', options.lon);
    params.append('radius', options.radius != null ? options.radius : 15);
    params.append('limit', options.limit || 50);
    params.append('offset', options.offset || 0);
    return this.request(`/api/customer/search/location?${params.toString()}`);
  }

  async getPopularDishes(limit = 20) {
    return this.request(`/api/customer/search/popular-dishes?limit=${limit}`);
  }

  async getCuisines() {
    return this.request('/api/customer/search/cuisines');
  }

  async searchKitchens(searchQuery, filters = {}) {
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (filters.city) params.append('city', filters.city);
    if (filters.zip_code) params.append('zip_code', filters.zip_code);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    return this.request(`/api/customer/discovery/kitchens?${params.toString()}`);
  }

  // ─── Cart ─────────────────────────────────────────────────────────────────

  async getCart() {
    return this.request('/api/customer/cart');
  }

  async addToCart(itemData) {
    return this.request('/api/customer/cart', { method: 'POST', body: itemData });
  }

  async updateCartItem(cartItemId, quantity) {
    return this.request(`/api/customer/cart/${cartItemId}`, {
      method: 'PUT',
      body: { quantity },
    });
  }

  async removeFromCart(cartItemId) {
    return this.request(`/api/customer/cart/${cartItemId}`, { method: 'DELETE' });
  }

  async clearCart() {
    return this.request('/api/customer/cart', { method: 'DELETE' });
  }

  // ─── Orders ───────────────────────────────────────────────────────────────

  async createOrder(orderData) {
    return this.request('/api/customer/orders', { method: 'POST', body: orderData });
  }

  async getOrders(status = null) {
    const qs = status ? `?status=${status}` : '';
    return this.request(`/api/customer/orders${qs}`);
  }

  async getOrderHistory(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.period) params.append('period', filters.period);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    const qs = params.toString();
    return this.request(`/api/customer/orders${qs ? `?${qs}` : ''}`);
  }

  async getOrderById(orderId) {
    return this.request(`/api/customer/orders/${orderId}`);
  }

  async cancelOrder(orderId, reason) {
    return this.request(`/api/customer/orders/${orderId}/cancel`, {
      method: 'POST',
      body: { reason },
    });
  }

  async submitOrderRating(orderId, { food_rating, restaurant_rating } = {}) {
    return this.request(`/api/customer/orders/${orderId}/rating`, {
      method: 'POST',
      body: { food_rating, restaurant_rating },
    });
  }

  // ─── Payment ──────────────────────────────────────────────────────────────

  async createStripeCheckoutSession(payload) {
    return this.request('/api/payment/create-checkout-session', {
      method: 'POST',
      body: payload,
    });
  }

  async getPaymentMethods() {
    return this.request('/api/customer/payment-methods');
  }

  async createPaymentMethod(data) {
    return this.request('/api/customer/payment-methods', { method: 'POST', body: data });
  }

  async deletePaymentMethod(id) {
    return this.request(`/api/customer/payment-methods/${id}`, { method: 'DELETE' });
  }

  // ─── Favorites ────────────────────────────────────────────────────────────

  async getFavorites() {
    return this.request('/api/customer/favorites');
  }

  async addToFavorites(kitchenId) {
    return this.request(`/api/customer/favorites/${kitchenId}`, { method: 'POST' });
  }

  async removeFromFavorites(kitchenId) {
    return this.request(`/api/customer/favorites/${kitchenId}`, { method: 'DELETE' });
  }

  // ─── Reviews ──────────────────────────────────────────────────────────────

  async getMyReviews() {
    return this.request('/api/customer/reviews');
  }

  async createReview(orderItemId, reviewData) {
    return this.request('/api/customer/reviews', {
      method: 'POST',
      body: { orderItemId, ...reviewData },
    });
  }

  // ─── Notifications ────────────────────────────────────────────────────────

  async getNotificationSettings() {
    return this.request('/api/customer/notifications/settings');
  }

  async updateNotificationSettings(settings) {
    return this.request('/api/customer/notifications/settings', {
      method: 'PUT',
      body: settings,
    });
  }

  // ─── Support ──────────────────────────────────────────────────────────────

  async getSupportFAQs() {
    return this.request('/api/support/faqs');
  }

  async createSupportTicket(payload) {
    return this.request('/api/support/tickets', { method: 'POST', body: payload });
  }

  async getSupportTickets({ status, page = 1, per_page = 20 } = {}) {
    const params = new URLSearchParams({ page, per_page });
    if (status) params.append('status', status);
    return this.request(`/api/support/tickets?${params.toString()}`);
  }

  async getSupportTicket(ticketId) {
    return this.request(`/api/support/tickets/${ticketId}`);
  }

  async addSupportMessage(ticketId, payload) {
    return this.request(`/api/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: payload,
    });
  }

  async getSupportMessages(ticketId) {
    return this.request(`/api/support/tickets/${ticketId}/messages`);
  }

  async healthCheck() {
    return this.request('/health');
  }
}

export default new ApiService();
